import { useEffect, useRef, useState } from "react";
import { Skull, Swords } from "lucide-react";

/**
 * Combat encounter panel. Rendered only when `run.current_enemy` + `enemy_hp > 0`.
 * Shows:
 *  - enemy nameplate + boss glow (if is_boss_room)
 *  - smoothly animated HP progress bar
 *  - floating damage number when enemy_hp drops
 *  - 1s strike overlay animation triggered from the parent via `strikeKey` prop
 *
 * State transitions come from the parent (Dashboard) which owns `run`. This
 * component just reacts to `hp` changes — no direct API calls.
 */
export default function CombatPanel({
  enemyName,
  hp,
  maxHp,
  hpDelta = 0,       // amount subtracted this turn (positive int)
  isBoss = false,
  strikeKey = 0,     // increment to re-trigger the strike animation
  portraitUrl = "",  // optional enemy portrait; renders a small silhouette when available
  crit = false,      // when true, overlay the CRIT! burst + screen-shake on the strike
}) {
  const [prevHp, setPrevHp] = useState(hp);
  const [floatingDmg, setFloatingDmg] = useState(null);
  const dmgTimer = useRef(null);

  useEffect(() => {
    if (hpDelta > 0) {
      setFloatingDmg({ value: hpDelta, id: strikeKey || Date.now(), crit });
      if (dmgTimer.current) clearTimeout(dmgTimer.current);
      dmgTimer.current = setTimeout(() => setFloatingDmg(null), crit ? 2000 : 1400);
    }
    setPrevHp(hp);
    return () => dmgTimer.current && clearTimeout(dmgTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hp, hpDelta, strikeKey, crit]);

  // Screen shake — briefly add a class to <body> so the whole viewport jolts.
  useEffect(() => {
    if (!crit || strikeKey <= 0) return;
    const el = document.body;
    el.classList.add("screen-crit-shake");
    const t = setTimeout(() => el.classList.remove("screen-crit-shake"), 650);
    return () => { clearTimeout(t); el.classList.remove("screen-crit-shake"); };
  }, [crit, strikeKey]);

  if (!enemyName || !maxHp || hp <= 0) return null;

  const pct = Math.max(0, Math.min(100, (hp / maxHp) * 100));
  const bossGlow = isBoss ? "boss-glow" : "";
  const shakeCls = strikeKey > 0 ? "enemy-shake" : "";

  return (
    <div
      key={`combat-${strikeKey}`}
      className={`panel p-4 relative overflow-hidden ${bossGlow} ${shakeCls}`}
      data-testid="combat-panel"
      style={isBoss ? { borderColor: "rgba(226,88,34,0.6)", boxShadow: "0 0 24px rgba(226,88,34,0.25) inset" } : {}}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {portraitUrl ? (
            <img
              src={portraitUrl.startsWith("http") ? portraitUrl : portraitUrl}
              alt=""
              className="w-12 h-12 object-cover flex-shrink-0 border border-white/15"
              style={isBoss ? { boxShadow: "0 0 12px rgba(226,88,34,0.5)" } : {}}
              data-testid="combat-enemy-portrait"
              onError={(e) => { e.target.style.display = "none"; }}
            />
          ) : (
            <div
              className="w-12 h-12 flex items-center justify-center flex-shrink-0 border border-white/10 bg-black/40"
              data-testid="combat-enemy-portrait-placeholder"
            >
              {isBoss ? <Skull size={20} className="ember" /> : <Swords size={20} className="blood-text" />}
            </div>
          )}
          <div className="flex flex-col min-w-0">
            <span className="text-[10px] tracking-[0.35em] ash-text">
              {isBoss ? "BOSS" : "ENEMY"}
            </span>
            <span
              className={`font-heading text-lg truncate ${isBoss ? "ember" : "blood-text"}`}
              data-testid="combat-enemy-name"
            >
              {enemyName.toUpperCase()}
            </span>
          </div>
        </div>
        <span className="text-xs ash-text flex-shrink-0" data-testid="combat-enemy-hp-text">
          {hp} / {maxHp} HP
        </span>
      </div>

      <div className="mt-3 relative h-[10px] bg-black/60 border border-white/10 overflow-hidden">
        <div
          data-testid="combat-enemy-hp-bar"
          className="h-full transition-all duration-700 ease-out"
          style={{
            width: `${pct}%`,
            background: isBoss
              ? "linear-gradient(90deg, #e25822, #8a0303)"
              : "linear-gradient(90deg, #ff6b3a, #8a0303)",
            boxShadow: "0 0 10px rgba(226,88,34,0.4) inset",
          }}
        />
      </div>

      {/* Cinematic strike burst — retriggered on every damaging swing */}
      {strikeKey > 0 && (
        <>
          <span
            key={`flash-${strikeKey}`}
            data-testid="combat-flash"
            className={`pointer-events-none absolute inset-0 ${crit ? "combat-flash-crit" : "combat-flash"}`}
            aria-hidden="true"
          />
          <span
            key={`strike-${strikeKey}`}
            data-testid="combat-strike"
            className="pointer-events-none absolute inset-0 combat-strike"
            aria-hidden="true"
          />
          <span
            key={`embers-${strikeKey}`}
            data-testid="combat-embers"
            className="pointer-events-none absolute inset-0 combat-embers"
            aria-hidden="true"
          />
        </>
      )}

      {/* CRIT! burst — huge gold-white shout that eclipses the panel briefly */}
      {crit && strikeKey > 0 && (
        <span
          key={`crit-${strikeKey}`}
          data-testid="combat-crit-burst"
          className="pointer-events-none absolute inset-0 flex items-center justify-center combat-crit-burst"
          aria-hidden="true"
        >
          <span
            className="font-heading italic tracking-[0.2em]"
            style={{
              fontSize: "clamp(3rem, 8vw, 6rem)",
              background: "linear-gradient(135deg, #fff2b0 0%, #d4af37 40%, #ff6b3a 80%, #fff2b0 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              textShadow: "0 0 24px rgba(255,242,176,0.75), 0 0 48px rgba(212,175,55,0.5)",
              filter: "drop-shadow(0 0 12px rgba(255,242,176,0.65))",
            }}
          >
            CRIT!
          </span>
        </span>
      )}

      {/* Floating damage number */}
      {floatingDmg && (
        <span
          key={`dmg-${floatingDmg.id}`}
          data-testid="combat-damage-float"
          className={`pointer-events-none absolute right-6 top-1 font-heading ${floatingDmg.crit ? "text-6xl combat-dmg-float-crit" : "text-3xl combat-dmg-float"}`}
          style={{
            color: floatingDmg.crit ? "#fff2b0" : "#ff6b3a",
            textShadow: floatingDmg.crit
              ? "0 0 20px rgba(255,242,176,0.9), 0 0 40px rgba(212,175,55,0.7)"
              : "0 0 14px rgba(255,107,58,0.8)",
          }}
        >
          -{floatingDmg.value}{floatingDmg.crit ? "!" : ""}
        </span>
      )}
    </div>
  );
}
